import { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { msalInstance, login, logout, getAccessToken, ensureMsalReady } from '../../lib/auth';
import { RequestAccess } from '../../pages/RequestAccess';

export type Role = 'pending' | 'user' | 'admin' | 'inactive';

export interface AuthState {
  role: Role;
  email: string;
  displayName: string;
}

const AuthContext = createContext<AuthState | null>(null);
export const useAuthState = (): AuthState => {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuthState must be inside <AuthGate>');
  return v;
};

interface Props {
  children: ReactNode;
}

export function AuthGate({ children }: Props) {
  const [status, setStatus] = useState<'loading' | 'unauth' | 'pending' | 'inactive' | 'ok'>('loading');
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      let redirectToken: string | null = null;
      try {
        await ensureMsalReady();
        const result = await msalInstance.handleRedirectPromise();
        if (result?.accessToken) {
          redirectToken = result.accessToken;
          msalInstance.setActiveAccount(result.account);
        }
      } catch (err: any) {
        setErrorMsg(err?.message ?? 'Sign-in failed');
        setStatus('unauth');
        return;
      }

      const accounts = msalInstance.getAllAccounts();
      if (!accounts.length) {
        setStatus('unauth');
        return;
      }

      try {
        // Prefer the token MSAL just gave us from the redirect; only fall back
        // to silent acquisition on a normal page load with a cached account.
        const token = redirectToken ?? await getAccessToken();
        // Visible diagnostic: log the audience/scope/oid that the API will see.
        try {
          const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          console.log('[AuthGate] token claims:', {
            aud: payload.aud,
            iss: payload.iss,
            scp: payload.scp,
            roles: payload.roles,
            oid: payload.oid,
            upn: payload.upn ?? payload.preferred_username,
            exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
          });
        } catch (e) {
          console.warn('[AuthGate] could not decode token:', e);
        }
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/check`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (res.status === 401) {
          setStatus('unauth');
          return;
        }

        // Read as text first so we can give useful messages for empty bodies
        // (e.g. the Function App is reachable but no handlers are registered).
        const bodyText = await res.text();
        if (!res.ok || !bodyText) {
          const detail = bodyText
            ? bodyText.slice(0, 200)
            : `Reports API responded ${res.status} with no body. The Function App may still be starting up — retry in a minute.`;
          setErrorMsg(detail);
          setStatus('unauth');
          return;
        }

        let data: any;
        try {
          data = JSON.parse(bodyText);
        } catch {
          setErrorMsg('Reports API returned a non-JSON response. Check Function App configuration.');
          setStatus('unauth');
          return;
        }

        const role: Role = data.role;

        const state: AuthState = {
          role,
          email: data.email,
          displayName: data.displayName,
        };
        setAuthState(state);

        if (role === 'pending')       setStatus('pending');
        else if (role === 'inactive') setStatus('inactive');
        else                          setStatus('ok');
      } catch (err: any) {
        setErrorMsg(err?.message ?? 'Failed to check access');
        setStatus('unauth');
      }
    })();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="text-text-muted text-sm">Loading…</div>
      </div>
    );
  }

  if (status === 'unauth') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="bg-surface border border-border rounded-xl p-10 max-w-md w-full text-center">
          <div className="text-2xl font-semibold text-text-primary mb-2">LAOSS Reports</div>
          <div className="text-sm text-text-muted mb-6">Internal reporting & analytics</div>
          {errorMsg && <div className="text-danger text-sm mb-4">{errorMsg}</div>}
          <button
            onClick={() => login()}
            className="bg-accent hover:bg-accent-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium"
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  if (status === 'pending' && authState) {
    return <RequestAccess email={authState.email} name={authState.displayName} onSignOut={logout} />;
  }

  if (status === 'inactive') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="bg-surface border border-border rounded-xl p-10 max-w-md w-full text-center">
          <div className="text-lg font-semibold text-text-primary mb-2">Access revoked</div>
          <div className="text-sm text-text-muted mb-6">Contact your administrator if this is unexpected.</div>
          <button
            onClick={() => logout()}
            className="text-sm text-text-secondary underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authState!}>
      {children}
    </AuthContext.Provider>
  );
}
